/* eslint-disable no-param-reassign */
/* global G, PLATFORM */
import { preload, useMemoRef } from 'platform/react/hook'
import { Suspense, useContext, useMemo } from 'react'
import settings from '@tenant/settings'
import AppContext from 'platform/react/context/application'
import ErrorBoundary from 'ui/Error'
import sequenceComponentFindProxy from 'lib/sequence/component/children/find'
import Section from 'ui/Element/Section'
import { Grid } from '@mui/material'

const { staticActions } = settings

/**
 * Maps each {@link staticActions} to an Object representing a component that can be used
 * to call an event handler with.
 *
 * @param {Object} session          the session
 * @param {Gaia.Adapter.Intl} intl  the intl adapter
 * @returns {Object[]|[]}
 * @private
 */
const _staticItems = (session, intl) => staticActions
  ?.filter(({ platforms }) => !platforms || platforms.includes(PLATFORM))
  ?.map(({ name, icon }) => ({
  key: `static.${name}`,
  options: {
    key: `static.${name}`,
    icon: icon || 'favorite',
    label: intl._t(`navigation.${name}`, {
      ns: 'custom',
      _key: `navigation.${name}`,
      defaultValue: name,
    }),
    events: {}, // Will get populated through provideEvents function
  },
  [G.CONFIGURATION]: {
    route: {
      module: 'static',
      action: 'text',
      ref: name,
    },
  },
})) || []

/**
 * Maps over `items` and provides events for each item that will be executed with
 * `module` as the module and the item itself as the component. Works very similar
 * to the {@link quickActions} hook.
 *
 * @param {Gaia.AppModule.Spec} module
 * @param {Object[]} items
 * @returns {Object[]}
 */
const provideEvents = (module, items) => {
  const actionComponent = module[G.STATE][G.ACTION][G.COMPONENT]
  const { static: component = {} } = actionComponent
    ? sequenceComponentFindProxy(actionComponent)
    : {}
  const { events = {} } = component[G.PROPS] || {}

  items.forEach((item) => {
    item.handlerNames = item.handlerNames || { ...events }
    const moduleEvents = module[G.EVENTS]
    if (events && moduleEvents) {
      Object.keys(item.handlerNames).forEach((name) => {
        const handlerName = item.handlerNames[name]
        const handlerExists = moduleEvents[name]
            && Object.prototype.hasOwnProperty.call(moduleEvents[name], handlerName)
        // todo: turn into pipe(hasNamespace, usesNamespace)
        handlerExists
          ? item.options.events[name] = moduleEvents[name][handlerName](module, item)
          : console.warn(
            'ATTENTION!!! missing required event handler for static action',
            item.key,
          )
      })
    }
  })

  return items
}

/**
 * Static Component
 *
 * Displays a list of `template` list items that is composed of static actions
 * defined in the tenant settings.
 *
 * @param {React.Ref} forwardedRef  incoming ref
 * @param {string} template         template to display as the list item
 * @param {Object[]} events         events for the list item component
 * @param {Object} props            additional props
 * @returns {JSX.Element}
 */
const Static = ({ forwardedRef, template, events, ...props }) => {
  const { ...templateProps } = props
  const { session, intl, session: { [G.MODULE]: module } } = useContext(AppContext)
  const Template = useMemo(() => preload(template), [template])
  const staticItems = useMemo(() => _staticItems(session, intl), [])
  const hydratedItems = useMemo(() => provideEvents(module, staticItems), [module, staticItems])

  return (
    <ErrorBoundary>
      <Suspense fallback={null}>
        <Grid ref={forwardedRef}>
          {hydratedItems.map((item, i) => (
            <Grid
              item
              key={item.key}
              component={Section}
              classes={i === 0 ? [] : ['borderTop']}
            >
              <Template
                {...item.options}
                {...templateProps}
              />
            </Grid>
          ))}
        </Grid>
      </Suspense>
    </ErrorBoundary>
  )
}

export default useMemoRef(Static, [])
